// Report Template editor page — separate from ReportConfigEdit.
//
// Loads the config for context (name + attached scope components), loads the
// per-config context schema, then chooses initial editor mode:
//
//   template_blocks set                       → Blocks mode, hydrate the tree
//   template_blocks null, template null       → Blocks mode, default tree
//   template_blocks null, template non-null   → run parser. Clean → Blocks
//                                                with parsed tree; partial →
//                                                Advanced with the raw string.
//
// Save sends both fields explicitly (Blocks → derived Jinja + tree; Advanced
// → null tree + raw string). The backend's `exclude_unset` makes omitting
// `template_blocks` a no-op, which is wrong for us, so we always send it.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Alert, Container, Group, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';

import {
  BlockEditor,
  CodeMirrorTemplate,
  EditorPreviewPane,
  GeneratedJinjaPanel,
  ModeBar,
  PageTitle,
  parseJinjaToBlocks,
  serialiseBlocks,
  TemplateEditorMode,
} from '@/components';
import { defaultBlockTree } from '@/components/TemplateEditor/block-tree';
import { useReportConfigs, useReportTemplate, useScopeComponents } from '@/hooks';
import { BlockTree, ReportConfig, TemplatePreviewResponse, validateBlockTree } from '@/types';

export function ReportConfigTemplateEditPage() {
  const { configId } = useParams<{ configId: string }>();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('team_id') ?? undefined;
  const navigate = useNavigate();

  const { getConfig, updateConfig } = useReportConfigs({ teamId, autoFetch: false });
  const { scopeComponents: allScopeComponents } = useScopeComponents();

  const [config, setConfig] = useState<ReportConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [mode, setMode] = useState<TemplateEditorMode>('blocks');
  const [tree, setTree] = useState<BlockTree>(defaultBlockTree());
  const [advancedTemplate, setAdvancedTemplate] = useState<string>('');
  // Set when the legacy template couldn't be cleanly parsed; the user can
  // still switch to Blocks, they'll just see some bits as Raw blocks.
  const [parseWarning, setParseWarning] = useState<string | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<TemplatePreviewResponse | null>(null);

  // Only the scope components actually attached to this config are relevant
  // for the palette and section blocks.
  const attachedScopeComponents = useMemo(() => {
    if (!config) {
      return [];
    }
    const attachedIds = new Set(config.components.map((c) => c.scopeComponentId));
    return allScopeComponents.filter((sc) => attachedIds.has(sc.id));
  }, [allScopeComponents, config]);

  const slugNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const sc of attachedScopeComponents) {
      map[sc.slug] = sc.name;
    }
    return map;
  }, [attachedScopeComponents]);

  const { contextSchema, preview } = useReportTemplate({ configId, teamId });

  // Initial load: decide mode based on the saved state.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current || !configId) {
      return;
    }
    loadedRef.current = true;
    (async () => {
      try {
        setLoading(true);
        const loaded = await getConfig(configId);
        setConfig(loaded);
        // Run full structural validation rather than the shallow shape check.
        // If the saved tree is malformed (e.g. an older schema, or backend
        // drift), we fall back to Advanced mode so the user can still recover
        // the underlying Jinja rather than landing on a crashed canvas.
        const blockValidation = validateBlockTree(loaded.templateBlocks);
        if (blockValidation.ok) {
          setMode('blocks');
          setTree(blockValidation.tree);
          setAdvancedTemplate(loaded.template ?? '');
        } else if (loaded.templateBlocks !== null && loaded.templateBlocks !== undefined) {
          // template_blocks was set but invalid — flag it.
          setMode('advanced');
          setAdvancedTemplate(loaded.template ?? '');
          setParseWarning(
            `Saved block tree is malformed (${blockValidation.errors[0]}). Falling back to advanced mode so you can recover from the raw Jinja.`
          );
        } else if (loaded.template) {
          // Try to convert; bail to Advanced if any unparseable bits.
          const { tree: parsed, hasUnparseable } = parseJinjaToBlocks(loaded.template);
          if (hasUnparseable) {
            setMode('advanced');
            setAdvancedTemplate(loaded.template);
            setParseWarning(
              "This template uses features (loops, conditionals, set, ...) that don't fit the visual editor."
            );
          } else {
            setMode('blocks');
            setTree(parsed);
            setAdvancedTemplate(loaded.template);
          }
        } else {
          // Brand new — start in Blocks with the default tree.
          setMode('blocks');
          setTree(defaultBlockTree());
          setAdvancedTemplate('');
        }
      } catch (err) {
        notifications.show({
          title: 'Could not load configuration',
          message: err instanceof Error ? err.message : 'Unknown error',
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [configId, getConfig]);

  // Currently-canonical Jinja string. In Blocks mode we serialise on every
  // render so the generated panel and preview stay in sync. In Advanced mode
  // it's just the raw editor content.
  const generatedJinja = useMemo(() => {
    if (mode === 'blocks') {
      return serialiseBlocks(tree, { scopeComponentNamesBySlug: slugNameMap });
    }
    return advancedTemplate;
  }, [advancedTemplate, mode, slugNameMap, tree]);

  const handleModeChange = useCallback(
    (next: TemplateEditorMode) => {
      if (next === mode) {
        return;
      }
      if (mode === 'blocks' && next === 'advanced') {
        // ModeBar gates this behind a confirmation modal — by the time we get
        // here the user has consented to the destructive switch.
        setAdvancedTemplate(generatedJinja);
        setMode('advanced');
        setParseWarning(null);
        return;
      }
      // Advanced → Blocks: run the parser.
      const { tree: parsed, hasUnparseable } = parseJinjaToBlocks(advancedTemplate);
      setTree(parsed);
      setMode('blocks');
      setParseWarning(
        hasUnparseable
          ? "Some parts couldn't be converted and will appear as raw Jinja blocks."
          : null
      );
    },
    [advancedTemplate, generatedJinja, mode]
  );

  const handleBack = useCallback(() => {
    if (!configId) {
      return;
    }
    const params = teamId ? `?team_id=${teamId}` : '';
    navigate(`/admin/report-configs/${configId}${params}`);
  }, [configId, navigate, teamId]);

  const handlePreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const result = await preview(generatedJinja);
      setPreviewResult(result);
    } catch (err) {
      setPreviewResult({
        rendered: null,
        errors: [
          {
            line: null,
            message: err instanceof Error ? err.message : 'Preview request failed',
          },
        ],
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [generatedJinja, preview]);

  const handleSave = useCallback(async () => {
    if (!configId) {
      return;
    }
    setSaving(true);
    try {
      const templateString = generatedJinja;
      const blocksPayload = mode === 'blocks' ? (tree as unknown as Record<string, unknown>) : null;
      await updateConfig(configId, {
        template: templateString,
        template_blocks: blocksPayload,
      });
      notifications.show({
        title: 'Template saved',
        message: 'The report template has been updated.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }, [configId, generatedJinja, mode, tree, updateConfig]);

  if (loading) {
    return (
      <Stack gap="xl">
        <PageTitle title="Edit Template" subtitle="Loading…" />
        <Container size="md" px={0}>
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        </Container>
      </Stack>
    );
  }

  if (!config) {
    return (
      <Stack gap="xl">
        <PageTitle title="Edit Template" subtitle="Configuration not found" />
      </Stack>
    );
  }

  return (
    <Stack gap="md" h="calc(100vh - 100px)">
      <PageTitle
        title="Edit Template"
        subtitle={`${config.name} — design how this configuration's reports are assembled.`}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          border: '1px solid var(--mantine-color-default-border)',
          borderRadius: 'var(--mantine-radius-md)',
          overflow: 'hidden',
          background: 'var(--mantine-color-body)',
        }}
      >
        <ModeBar
          mode={mode}
          onModeChange={handleModeChange}
          onBack={handleBack}
          onPreview={handlePreview}
          onSave={handleSave}
          saving={saving}
        />

        {parseWarning && (
          <Alert
            variant="light"
            color="orange"
            icon={<IconAlertTriangle size={16} />}
            radius={0}
            withCloseButton
            onClose={() => setParseWarning(null)}
          >
            {parseWarning}
          </Alert>
        )}

        <div
          style={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
            {mode === 'blocks' ? (
              <BlockEditor
                tree={tree}
                onChange={setTree}
                scopeComponents={attachedScopeComponents}
                contextSchema={contextSchema}
              />
            ) : (
              <div style={{ flex: 1, padding: 'var(--mantine-spacing-sm)' }}>
                <CodeMirrorTemplate
                  value={advancedTemplate}
                  onChange={setAdvancedTemplate}
                  errors={[]}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  Edit the raw Jinja directly. Switch back to Blocks to use the visual editor —
                  anything the parser can&apos;t convert will appear as a Raw block.
                </Text>
              </div>
            )}
          </div>

          {previewOpen && (
            <EditorPreviewPane
              loading={previewLoading}
              result={previewResult}
              onClose={() => setPreviewOpen(false)}
            />
          )}
        </div>

        <GeneratedJinjaPanel source={generatedJinja} />
      </div>
    </Stack>
  );
}
